import { getAuthToken } from '../../../../services/auth/tokenStorage';
import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { obituaryDetailsService } from '../../../../services/obituaryDetailsInfo';
import { API_URL } from '../../../../config';

import { createLogger } from '../../../../utils/logger';
import { ensureAiConsent } from '../../../../services/ai/aiConsent';
const logger = createLogger('ObituaryDetailsInput');

type ObituaryDetailsInputScreenRouteProp = RouteProp<RootStackParamList, 'ObituaryDetailsInput'>;

export const ObituaryDetailsInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<ObituaryDetailsInputScreenRouteProp>();

  const { obituaryDetailsData, isEditing } = route.params || {};

  // All form fields state
  const [education, setEducation] = useState(obituaryDetailsData?.educationBackground || '');
  const [career, setCareer] = useState(obituaryDetailsData?.careerHighlights || '');
  const [military, setMilitary] = useState(obituaryDetailsData?.militaryService || '');
  const [family, setFamily] = useState(obituaryDetailsData?.familyMentions || '');
  const [hobbies, setHobbies] = useState(obituaryDetailsData?.hobbiesInterests || '');
  const [community, setCommunity] = useState(obituaryDetailsData?.charitableWork || '');
  const [personal, setPersonal] = useState(obituaryDetailsData?.keyAccomplishments || '');
  const [obituaryContent, setObituaryContent] = useState(obituaryDetailsData?.notes || '');

  const [isLoading, setIsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!obituaryContent.trim() && !education.trim() && !career.trim() && !personal.trim()) {
      Alert.alert('Error', 'Please enter at least some obituary information');
      return;
    }

    try {
      setSaving(true);

      const obituaryUpdateData = {
        educationBackground: education.trim(),
        careerHighlights: career.trim(),
        militaryService: military.trim(),
        familyMentions: family.trim(),
        hobbiesInterests: hobbies.trim(),
        charitableWork: community.trim(),
        keyAccomplishments: personal.trim(),
        notes: obituaryContent.trim(),
        obituaryPreference: 'yes', // Setting default preference when content is added
      };

      let savedObituary;

      if (isEditing && obituaryDetailsData?.id) {
        // Update existing record
        savedObituary = await obituaryDetailsService.updateObituaryDetails(
          obituaryDetailsData.id,
          obituaryUpdateData
        );
        // Handle wrapped response format { success, data, message }
        const updatedData = savedObituary?.data || savedObituary;
        logger.debug('Obituary details updated successfully with ID:', updatedData.id);

        // Navigate to lock/unlock screen to allow re-selection of lock/unlock
        navigation.navigate('ObituaryDetailsLockUnlock', { obituaryDetailsId: updatedData.id });
      } else {
        // Create new record
        savedObituary = await obituaryDetailsService.createObituaryDetails(obituaryUpdateData);
        // Handle wrapped response format { success, data, message }
        const createdData = savedObituary?.data || savedObituary;
        logger.debug('Obituary details saved successfully with ID:', createdData.id);

        // Navigate to lock/unlock screen with the ID
        navigation.navigate('ObituaryDetailsLockUnlock', { obituaryDetailsId: createdData.id });
      }
    } catch (error) {
      logger.error('Error saving obituary details:', error);
      Alert.alert('Error', 'Failed to save obituary details. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleAIAssistance = async (mode: 'write' | 'improve') => {
    if (!(await ensureAiConsent())) return;
    try {
      if (mode === 'improve' && !obituaryContent.trim()) {
        Alert.alert('Error', 'Please write some content first before improving.');
        return;
      }

      setIsLoading(true);
      logger.debug('Making AI request with mode:', mode);

      const token = await getAuthToken();
      if (!token) {
        Alert.alert('Error', 'Please log in to use AI assistance.');
        return;
      }

      const response = await fetch(`${API_URL}/api/ai/generate/obituary`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          mode,
          content: mode === 'improve' ? obituaryContent : undefined,
          context: {
            education: education.trim() || undefined,
            career: career.trim() || undefined,
            military: military.trim() || undefined,
            family: family.trim() || undefined,
            hobbies: hobbies.trim() || undefined,
            community: community.trim() || undefined,
            personal: personal.trim() || undefined,
          },
        }),
      });

      const data = await response.json();
      logger.debug('API response received:', data);

      if (data.success && data.content) {
        setObituaryContent(data.content);
      } else {
        Alert.alert('Error', data.error || 'No response received from AI. Please try again.');
      }
    } catch (error: any) {
      logger.error('AI assistance error:', error);
      Alert.alert('Error', 'Failed to get AI assistance. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>
          Write Your Obituary Details
        </Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} keyboardShouldPersistTaps="handled" automaticallyAdjustKeyboardInsets={true}>
          <Text style={styles.sectionTitle}>Education</Text>
          <TextInput
            style={styles.input}
            placeholder="Schools attended, degrees earned"
            value={education}
            onChangeText={setEducation}
            multiline
            textAlignVertical="top"
          />

          <Text style={styles.sectionTitle}>Career</Text>
          <TextInput
            style={styles.input}
            placeholder="Notable jobs, positions, and achievements"
            value={career}
            onChangeText={setCareer}
            multiline
            textAlignVertical="top"
          />

          <Text style={styles.sectionTitle}>Military Service</Text>
          <TextInput
            style={styles.input}
            placeholder="Branch, rank, and honors (if applicable)"
            value={military}
            onChangeText={setMilitary}
            multiline
            textAlignVertical="top"
          />

          <Text style={styles.sectionTitle}>Family</Text>
          <TextInput
            style={styles.input}
            placeholder="Children, Step Children and/or key life events"
            value={family}
            onChangeText={setFamily}
            multiline
            textAlignVertical="top"
          />

          <Text style={styles.sectionTitle}>Hobbies & Interests</Text>
          <TextInput
            style={styles.input}
            placeholder="Passions, pastimes, or unique skills"
            value={hobbies}
            onChangeText={setHobbies}
            multiline
            textAlignVertical="top"
          />

          <Text style={styles.sectionTitle}>Community Service</Text>
          <TextInput
            style={styles.input}
            placeholder="Memberships, volunteer work, or charitable contributions"
            value={community}
            onChangeText={setCommunity}
            multiline
            textAlignVertical="top"
          />

          <Text style={styles.sectionTitle}>Personal Legacy</Text>
          <TextInput
            style={styles.input}
            placeholder="What made you unique, your values, or how you impacted others"
            value={personal}
            onChangeText={setPersonal}
            multiline
            textAlignVertical="top"
          />

          <View style={[styles.aiButtonsContainer, { marginTop: 30 }]}>
            <TouchableOpacity
              style={[styles.aiButton, { backgroundColor: '#29BFC0' }]}
              onPress={() => handleAIAssistance('write')}
              disabled={isLoading}
            >
              {isLoading ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.aiButtonText}>Get AI Help Writing</Text>
              )}
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.aiButton, { backgroundColor: '#FF9F64' }]}
              onPress={() => handleAIAssistance('improve')}
              disabled={isLoading}
            >
              {isLoading ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.aiButtonText}>Improve with AI</Text>
              )}
            </TouchableOpacity>
          </View>

          <Text style={[styles.sectionTitle, { marginTop: 30 }]}>Write Your Obituary Here</Text>
          <TextInput
            style={[styles.input, { minHeight: 200 }]}
            placeholder="Write your complete obituary here..."
            value={obituaryContent}
            onChangeText={setObituaryContent}
            multiline
            textAlignVertical="top"
          />

          <TouchableOpacity
            style={[styles.saveButton, saving && { opacity: 0.7 }]}
            onPress={handleSave}
            disabled={saving}
          >
            {saving ? (
              <ActivityIndicator color="#FFFFFF" />
            ) : (
              <Text style={styles.saveButtonText}>{isEditing ? 'Update' : 'Save'}</Text>
            )}
          </TouchableOpacity>

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
   backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  aiButtonsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: ms(20),
    gap: ms(10),
  },
  aiButton: {
    flex: 1,
    padding: ms(12),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  aiButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  sectionTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(10),
    marginTop: ms(20),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    padding: ms(15),
    marginBottom: ms(20),
    minHeight: s(60),
    textAlignVertical: 'top',
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  label: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(10),
  },
  letterInput: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    padding: ms(15),
    marginBottom: ms(30),
    minHeight: s(300),
    textAlignVertical: 'top',
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
  },
  saveButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
    marginBottom: ms(30),
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  bottomPadding: {
    height: s(120),
  },
});

export default ObituaryDetailsInputScreen;
